<template>
  <section v-if="issue && issue.id" class="issue-responsibles pr-4">
    <header class="issue-responsibles__header">
      <div class="issue-responsibles__title-wrap">
        <b-icon pack="fas" icon="users" size="is-small" class="issue-responsibles__title-icon" />
        <h3 class="issue-responsibles__title">{{ $t('issue_users') }}</h3>
        <span v-if="totalUsersCount" class="issue-responsibles__count">{{ totalUsersCount }}</span>
      </div>
    </header>

    <div class="issue-responsibles__card">
      <div v-if="activityManagerUser" class="issue-responsibles__group">
        <div class="issue-responsibles__group-label">{{ $t('activity_manager') }}</div>
        <div class="issue-responsibles__tags">
          <span class="issue-responsibles__tag">{{ activityManagerUser.fullname }}</span>
        </div>
      </div>

      <div v-if="creatorUser" class="issue-responsibles__group">
        <div class="issue-responsibles__group-label">{{ $t('issue_creator') }}</div>
        <div class="issue-responsibles__tags">
          <span class="issue-responsibles__tag">{{ creatorUser.fullname }}</span>
        </div>
      </div>

      <template v-if="issueManagersUsers.length">
        <div v-if="activityManagerUser || creatorUser" class="issue-responsibles__divider" />

        <div class="issue-responsibles__group">
          <div class="issue-responsibles__group-label">{{ $t('issue_managers') }}</div>
          <div class="issue-responsibles__tags">
            <span
              v-for="user in visibleIssueManagersUsers"
              :key="'manager-' + user.user_id"
              class="issue-responsibles__tag"
            >
              {{ user.fullname }}
            </span>

            <button
              v-if="hasHiddenIssueManagers"
              type="button"
              class="issue-responsibles__more-tag"
              @click="showAllIssueManagers = !showAllIssueManagers"
            >
              {{ showAllIssueManagers ? $t('hide') : $t('see_more') }}
              <b-icon
                pack="fas"
                :icon="showAllIssueManagers ? 'chevron-up' : 'chevron-down'"
                size="is-small"
              />
            </button>
          </div>
        </div>
      </template>

      <template v-if="resolutionUsers.length">
        <div class="issue-responsibles__divider" />

        <div class="issue-responsibles__group">
          <div class="issue-responsibles__group-label">{{ $t('issue_resolutors') }}</div>
          <div class="issue-responsibles__tags">
            <span
              v-for="user in visibleResolutionUsers"
              :key="'resolution-' + user.user_id"
              class="issue-responsibles__tag"
            >
              {{ user.fullname }}
            </span>

            <button
              v-if="hasHiddenResolutionUsers"
              type="button"
              class="issue-responsibles__more-tag"
              @click="showAllResolutors = !showAllResolutors"
            >
              {{ showAllResolutors ? $t('hide') : $t('see_more') }}
              <b-icon
                pack="fas"
                :icon="showAllResolutors ? 'chevron-up' : 'chevron-down'"
                size="is-small"
              />
            </button>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
export default {
  name: 'IssueHomeUsers',

  props: {
    issue: { type: Object, required: true },
    project: { type: Object, default: null },
  },

  data: () => ({
    showAllIssueManagers: false,
    showAllResolutors: false,
    previewLimit: 7,
  }),

  computed: {
    activityManagerUser() {
      if (this.issue.activity && this.issue.activity.manager) {
        return this.normalizeUser(this.issue.activity.manager)
      }

      return null
    },

    creatorUser() {
      return this.normalizeUser(this.issue.creator)
    },

    resolutionUsers() {
      if (!this.project || this.project.issue_template != 1) {
        return []
      }

      return (this.issue.issue_resolutors || [])
        .map(user => this.normalizeUser(user))
        .filter(Boolean)
    },

    resolutionUserIds() {
      return new Set(this.resolutionUsers.map(user => user.user_id))
    },

    featuredUserIds() {
      const ids = new Set(this.resolutionUserIds)

      if (this.activityManagerUser) {
        ids.add(this.activityManagerUser.user_id)
      }

      if (this.creatorUser) {
        ids.add(this.creatorUser.user_id)
      }

      return ids
    },

    issueManagersUsers() {
      const users = []
      const seen = new Set(this.featuredUserIds)
      const sources = [
        this.issue.manager,
        ...(this.issue.issue_admins || []),
        ...(this.issue.participants || []),
        ...(this.issue.collaborators || []),
      ]

      sources.forEach((user) => {
        const normalized = this.normalizeUser(user)
        if (normalized && !seen.has(normalized.user_id)) {
          users.push(normalized)
          seen.add(normalized.user_id)
        }
      })

      return users
    },

    totalUsersCount() {
      const seen = new Set()

      if (this.activityManagerUser) {
        seen.add(this.activityManagerUser.user_id)
      }

      if (this.creatorUser) {
        seen.add(this.creatorUser.user_id)
      }

      this.issueManagersUsers.forEach(user => seen.add(user.user_id))
      this.resolutionUsers.forEach(user => seen.add(user.user_id))

      return seen.size
    },

    hasHiddenIssueManagers() {
      return this.issueManagersUsers.length > this.previewLimit
    },

    hasHiddenResolutionUsers() {
      return this.resolutionUsers.length > this.previewLimit
    },

    visibleIssueManagersUsers() {
      if (this.showAllIssueManagers || !this.hasHiddenIssueManagers) {
        return this.issueManagersUsers
      }

      return this.issueManagersUsers.slice(0, this.previewLimit)
    },

    visibleResolutionUsers() {
      if (this.showAllResolutors || !this.hasHiddenResolutionUsers) {
        return this.resolutionUsers
      }

      return this.resolutionUsers.slice(0, this.previewLimit)
    },
  },

  methods: {
    normalizeUser(user) {
      if (!user) {
        return null
      }

      const userId = user.user_id || user.id
      if (!userId) {
        return null
      }

      return {
        user_id: userId,
        fullname: user.fullname || `${user.name || ''} ${user.surname || ''}`.trim() || '-',
      }
    },
  },
}
</script>

<style scoped>
.issue-responsibles {
  margin-bottom: 1rem;
}

.issue-responsibles__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.issue-responsibles__title-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.issue-responsibles__title-icon {
  color: #7d99d4;
}

.issue-responsibles__title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #2c5282;
}

.issue-responsibles__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #ebf4ff;
  color: #2b6cb0;
  font-size: 0.6875rem;
  font-weight: 700;
}

.issue-responsibles__card {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.875rem;
}

.issue-responsibles__group + .issue-responsibles__group {
  margin-top: 0.75rem;
}

.issue-responsibles__group-label {
  margin-bottom: 0.5rem;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #a0aec0;
}

.issue-responsibles__divider {
  height: 1px;
  background: #edf2f7;
  margin: 0.875rem 0;
}

.issue-responsibles__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.issue-responsibles__tag {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.55rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #faf5f0;
  color: #744210;
  font-size: 0.75rem;
  font-weight: 600;
}

.issue-responsibles__more-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.3rem 0.55rem;
  border: 1px dashed #90cdf4;
  border-radius: 0.25rem;
  background: #ebf8ff;
  color: #2b6cb0;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
}

.issue-responsibles__more-tag:hover {
  background: #bee3f8;
  border-color: #63b3ed;
}
</style>
